import Stars from '../components/Stars';
import InventoryTerms from '../components/inventory/InventoryTerms';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { createOrder, getDeliveryOptions, validatePromoCode } from '../api';
import { deliveryOptions } from '../data/constants';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartSidebar from '../components/CartSidebar';
import LoginModal from '../components/LoginModal';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getCartTotal, clearCart, validateCart, cartError, checkingCart, hasUnavailable, setIsCartOpen } = useCart();
  const { user, refreshUser, setIsLoginModalOpen } = useAuth();
  const [deliveryList, setDeliveryList] = useState(deliveryOptions);

  useEffect(() => {
    getDeliveryOptions().then(setDeliveryList).catch(error => console.error('Failed to load delivery options:', error));
  }, []);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    email: user?.email || '',
    telegram: '',
    delivery: 'pickup',
    address: '',
    city: '',
    promoCode: '',
    useCoins: false
  });

  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const submitLock = useRef(false);
  const [pendingOrder,setPendingOrder] = useState(()=>{try{return JSON.parse(sessionStorage.getItem('tistore_pending_order')||'null')}catch{return null}});
  const [inventoryOffer,setInventoryOffer] = useState(null);
  const [inventoryVersion,setInventoryVersion] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedDelivery = deliveryList.find(d => d.id === formData.delivery);
  const cartTotal = getCartTotal();
  const deliveryPrice = selectedDelivery?.price || 0;

  // Calculate promo discount
  let promoDiscount = 0;
  if (appliedPromo) {
    if (appliedPromo.type === 'percent') {
      promoDiscount = Math.floor(cartTotal * (appliedPromo.value / 100));
    } else {
      promoDiscount = appliedPromo.value;
    }
  }
  promoDiscount = Math.min(promoDiscount, cartTotal);

  const maxCoinsToUse = user ? Math.min(user.coins || 0, Math.floor(cartTotal * 0.15), Math.max(0, cartTotal - promoDiscount)) : 0;
  const coinsDiscount = formData.useCoins ? maxCoinsToUse : 0;
  const coinsEarnedEstimate = user ? Math.floor(Math.max(0, cartTotal - promoDiscount - coinsDiscount) * 0.10) : 0;

  const finalTotal = cartTotal + deliveryPrice - promoDiscount - coinsDiscount;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyPromo = async () => {
    if (!formData.promoCode) return;
    
    try {
      const promo = await validatePromoCode(formData.promoCode, cartTotal);
      setAppliedPromo(promo);
      setPromoError('');
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Промокод не найден');
      setAppliedPromo(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current=true;setIsSubmitting(true);
    try {
      if (!pendingOrder) {
        if (checkingCart || !inventoryOffer || inventoryOffer.lines.some(l=>l.blocked) || !(await validateCart())) return;
        if (!formData.name || !formData.phone) { alert('Заполните имя и телефон'); return; }
        if (selectedDelivery?.requires_address && !formData.address) {alert('Укажите адрес доставки');return;}
        if (selectedDelivery?.requires_city && !formData.city) {alert('Выберите город доставки');return;}
      }
      const orderData = pendingOrder || {
        operation_id: crypto.randomUUID(),
        accepted_inventory_signature: inventoryOffer.signature,
        customer: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email || null,
          telegram: formData.telegram || null
        },
        items: cart.map(item => ({
          product_id: item.id,
          article: item.article,
          name: item.name,
          size: item.size,
          price: item.price,
          quantity: item.quantity,
          image: item.image || ''
        })),
        delivery_type: formData.delivery,
        delivery_address: formData.address || null,
        delivery_city: formData.city || null,
        promo_code: appliedPromo?.code || null,
        use_coins: formData.useCoins,
        user_id: user?.id || null
      };

      sessionStorage.setItem('tistore_pending_order',JSON.stringify(orderData));setPendingOrder(orderData);
      const createdOrder = await createOrder(orderData);
      sessionStorage.removeItem('tistore_pending_order');setPendingOrder(null);
      sessionStorage.setItem('tistore_last_order', JSON.stringify(createdOrder));
      clearCart();
      if (user) await refreshUser();
      navigate('/order-success', { state: { order: createdOrder } });
    } catch(error) {
      if (error.response && error.response.status < 500) {
        sessionStorage.removeItem('tistore_pending_order');setPendingOrder(null);
        setInventoryVersion(v=>v+1);await validateCart();
      }
      alert(typeof error.response?.data?.detail==='string'?error.response.data.detail:'Связь прервалась. Нажмите «Проверить оформление»: повторный запрос не создаст второй заказ.');
    } finally {setIsSubmitting(false);submitLock.current=false;}
  };

  if (cart.length === 0 && !pendingOrder) {
    return (
      <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
        <Header />
        <main className="pt-24 pb-24">
          <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center py-16">
              <h1 className="text-2xl font-medium mb-4 text-brand-text dark:text-brand-text">Корзина пуста</h1>
              <Button
                onClick={() => navigate('/')}
                className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface"
              >
                Перейти к товарам
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
      <Header />
      <CartSidebar />
      <LoginModal />

      <main className="pt-20 md:pt-24 pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1 className="text-2xl md:text-3xl font-medium mb-8 text-brand-text dark:text-brand-text">Оформление заказа</h1>

          <form onSubmit={handleSubmit} className="space-y-8">
            <InventoryTerms items={cart} onQuote={setInventoryOffer} version={inventoryVersion}/>
            {cartError && <div role="alert" className="p-4 border border-brand-accent rounded-xl">{cartError} <button type="button" className="underline" onClick={() => setIsCartOpen(true)}>Вернуться к корзине</button></div>}
            {/* Contact Info */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-brand-text dark:text-brand-text">Контактные данные</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-brand-text dark:text-brand-text">ФИО *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Иван Иванов"
                    required
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-brand-text dark:text-brand-text">Телефон *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+7 999 123 45 67"
                    required
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-brand-text dark:text-brand-text">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email для уведомлений о заказе"
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telegram" className="text-brand-text dark:text-brand-text">Telegram</Label>
                  <Input
                    id="telegram"
                    name="telegram"
                    value={formData.telegram}
                    onChange={handleInputChange}
                    placeholder="@username"
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
              </div>
            </div>

            {/* Delivery */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-brand-text dark:text-brand-text">Способ получения</h2>
              <RadioGroup
                value={formData.delivery}
                onValueChange={(value) => setFormData(prev => ({ ...prev, delivery: value, address: '', city: '' }))}
                className="space-y-3"
              >
                {deliveryList.map(option => (
                  <label
                    key={option.id}
                    htmlFor={option.id}
                    className={`flex items-start space-x-3 p-4 border rounded-lg hover:border-brand-border dark:hover:border-brand-border transition-colors cursor-pointer ${
                      formData.delivery === option.id 
                        ? 'border-black dark:border-white bg-brand-surface dark:bg-brand-surface' 
                        : 'border-brand-border dark:border-brand-border'
                    }`}
                  >
                    <RadioGroupItem value={option.id} id={option.id} className="mt-1" />
                    <div className="flex-1">
                      <span className="font-medium text-brand-text dark:text-brand-text">
                        {option.name}
                        {option.price > 0 && (
                          <span className="ml-2 text-brand-text dark:text-brand-text">+{option.price} ₽</span>
                        )}
                      </span>
                      {option.description && <p className="text-sm text-brand-text dark:text-brand-text mt-1">{option.description}</p>}
                    </div>
                  </label>
                ))}
              </RadioGroup>

              {selectedDelivery?.requires_address && (
                <div className="space-y-2 pl-4">
                  <Label htmlFor="address" className="text-brand-text dark:text-brand-text">Адрес доставки *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Улица, дом, квартира"
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
              )}

              {selectedDelivery?.requires_city && (
                <div className="space-y-2 pl-4">
                  <Label htmlFor="city" className="text-brand-text dark:text-brand-text">Город доставки *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Введите город"
                    className="dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                  />
                </div>
              )}
            </div>
            
            {/* Бонусная программа */}
            {!user && (
              <div className="bg-brand-surface dark:bg-brand-accent/20 border border-brand-border dark:border-brand-border rounded-lg p-4 mb-6">
                <p className="text-brand-text dark:text-brand-text text-sm md:text-base">
                    Чтобы получать 10% старсами за покупки и оплачивать ими до 15% следующего заказа, нужно{' '}
                  <button
                    type="button"
                    onClick={() => setIsLoginModalOpen(true)}
                    className="font-medium text-brand-text dark:text-brand-text hover:underline focus:outline-none"
                  >
                    войти в аккаунт
                  </button>
                  .
                </p>
              </div>
            )}

            {/* Promo Code */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-brand-text dark:text-brand-text">Промокод</h2>
              <div className="flex gap-2">
                <Input
                  name="promoCode"
                  value={formData.promoCode}
                  onChange={handleInputChange}
                  placeholder="Введите промокод"
                  className="flex-1 dark:bg-brand-surface dark:border-brand-border dark:text-brand-text"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleApplyPromo}
                  className="dark:border-brand-border dark:text-brand-text dark:hover:bg-brand-surface"
                >
                  Применить
                </Button>
              </div>
              {promoError && <p className="text-brand-text text-sm">{promoError}</p>}
              {appliedPromo && (
                <p className="text-brand-text dark:text-brand-text text-sm">
                  Промокод {appliedPromo.code} применён: скидка {appliedPromo.type === 'percent' ? `${appliedPromo.value}%` : `${appliedPromo.value} ₽`}
                </p>
              )}
            </div>

            {/* Coins */}
            {user && (user.coins || 0) > 0 && (
              <div className="space-y-4">
                <h2 className="text-lg font-medium text-brand-text dark:text-brand-text">Старсы</h2>
                <div className="flex items-center gap-3 p-4 bg-brand-surface dark:bg-brand-surface rounded-lg">
                  <input
                    type="checkbox"
                    id="useCoins"
                    checked={formData.useCoins}
                    onChange={(e) => setFormData(prev => ({ ...prev, useCoins: e.target.checked }))}
                    className="w-5 h-5"
                  />
                  <Label htmlFor="useCoins" className="cursor-pointer text-brand-text dark:text-brand-text">
                    Использовать до <Stars value={maxCoinsToUse} /> (максимум 15% заказа)
                  </Label>
                  <span className="ml-auto text-sm text-brand-text dark:text-brand-text">Баланс: <Stars value={user.coins} /></span>
                </div>
                <p className="text-sm text-brand-text dark:text-brand-text">После получения заказа начислим примерно <Stars value={coinsEarnedEstimate} /> (10% покупки). Старсами можно оплатить до 15% следующего заказа.</p>
              </div>
            )}

            {/* Order Summary */}
            <div className="border-t dark:border-brand-border pt-6 space-y-3">
              <h2 className="text-lg font-medium mb-4 text-brand-text dark:text-brand-text">Ваш заказ</h2>
              {cart.map(item => (
                <div key={`${item.id}-${item.size}`} className="flex justify-between text-sm text-brand-text dark:text-brand-text">
                  <span>{item.name} ({item.size}) x {item.quantity}{item.unavailable && <b className="block text-red-300">{item.unavailable}</b>}</span>
                  <span>{(item.price * item.quantity).toLocaleString('ru-RU')} ₽</span>
                </div>
              ))}
              <div className="border-t dark:border-brand-border pt-3 space-y-2">
                <div className="flex justify-between text-brand-text dark:text-brand-text">
                  <span>Товары:</span>
                  <span>{cartTotal.toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="flex justify-between text-brand-text dark:text-brand-text">
                  <span>Доставка:</span>
                  <span>{deliveryPrice === 0 ? 'Бесплатно' : `${deliveryPrice} ₽`}</span>
                </div>
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-brand-text dark:text-brand-text">
                    <span>Скидка по промокоду:</span>
                    <span>-{promoDiscount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                {coinsDiscount > 0 && (
                  <div className="flex justify-between text-brand-text dark:text-brand-text">
                    <span>Списание старсов:</span>
                    <span>-{coinsDiscount.toLocaleString('ru-RU')} ₽</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-semibold pt-2 border-t dark:border-brand-border text-brand-text dark:text-brand-text">
                  <span>Итого:</span>
                  <span>{finalTotal.toLocaleString('ru-RU')} ₽</span>
                </div>
              </div>
            </div>

            <p className="text-sm leading-relaxed">Для оформления и исполнения заказа обрабатываем указанные вами контактные данные и данные доставки. Подробнее — в <a href="/privacy_policy" target="_blank" rel="noreferrer" className="underline">политике обработки данных</a>. <a href="/purchase-info" target="_blank" rel="noreferrer" className="underline">Информация о продавце, доставке и возврате</a>. Согласие на аналитику не требуется для покупки.</p>
            {pendingOrder&&<p role="status">Есть незавершённый запрос оформления. Проверим его результат с прежним составом и контактами, прежде чем создавать новый заказ.</p>}
            <Button
              type={pendingOrder ? "button" : "submit"}
              onClick={pendingOrder ? handleSubmit : undefined}
              className="w-full h-14 bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface text-base font-medium"
              disabled={isSubmitting || (!pendingOrder && (checkingCart || hasUnavailable || !inventoryOffer || inventoryOffer.lines.some(l=>l.blocked)))}
            >
              {isSubmitting ? 'Оформление...' : pendingOrder ? 'Проверить оформление' : 'Оформить заказ'}
            </Button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
